<template>
  <div class="row q-col-gutter-md">
    <div class="row col-12">
      <weekday-picker
        v-model="appointmentTiming"
        allow-date
        class="full-width"
        @update:model-value="onInputAppointmentTiming"
      />
    </div>
    <div class="col-6">
      <time-input
        v-model="scheduledStartTimestamp"
        allow-empty
        preserve-date
        :label="$t('sessions.modals.scheduleSession.timeInputLabel')"
      />
    </div>
    <div class="col-6">
      <time-input
        v-model="scheduledEndTimestamp"
        allow-empty
        preserve-date
        :label="$t('sessions.modals.scheduleSession.timeFinishInputLabel')"
      />
    </div>
    <div class="col-12">
      <client-users-selector
        v-model="userInfo"
        :skip-roles="['parent']"
        :session-id="sessionId"
        :client="client.id"
        :readonly="!client"
      />
    </div>
  </div>
</template>
<script>
import ClientUsersSelector from 'components/UI/ClientUsersSelector';
import TimeInput from 'components/UI/TimeInput';
import WeekdayPicker from 'components/UI/WeekdayPicker';
import { Timestamp } from 'firebase/firestore';
import { get, isArray, isEmpty } from 'lodash';
import { mapActions, mapState } from 'pinia';
import { useTime } from 'src/composables/time';
import AppointmentsMixin from 'src/mixins/AppointmentsMixin';
import { units } from 'src/store/units';

export default {
  name: 'EditScheduledAppointment',

  components: { WeekdayPicker, ClientUsersSelector, TimeInput },

  mixins: [AppointmentsMixin],

  props: {
    modelValue: {
      type: Object,
      default: null,
    },

    client: {
      type: Object,
      default: null,
    },

    sessionId: {
      type: String,
      required: true,
    },
  },

  emits: ['update:modelValue'],

  setup() {
    const { adjustDate, getHours, getMinutes, getSeconds, addToDate, getDateDiff } = useTime();

    return { adjustDate, getHours, getMinutes, getSeconds, addToDate, getDateDiff };
  },

  computed: {
    ...mapState(units, ['units']),

    appointmentTiming: {
      get() {
        const recurringWeekdays = get(this.modelValue, 'recurringWeekdays');
        if (isEmpty(recurringWeekdays)) {
          return get(this.modelValue, 'scheduledStartTimestamp');
        }
        return recurringWeekdays;
      },
      set(appointmentTiming) {
        if (appointmentTiming instanceof Timestamp) {
          let scheduledStartTimestampJs = appointmentTiming.toDate();
          if (this.scheduledStartTimestamp instanceof Timestamp) {
            const existingScheduledStartTimestamp = this.scheduledStartTimestamp.toDate();
            scheduledStartTimestampJs = this.adjustDate(scheduledStartTimestampJs, {
              hour: this.getHours(existingScheduledStartTimestamp),
              minute: this.getMinutes(existingScheduledStartTimestamp),
              second: this.getSeconds(existingScheduledStartTimestamp),
            });
          }
          const scheduledStartTimestamp = Timestamp.fromDate(scheduledStartTimestampJs);
          this.updateActivity({ scheduledStartTimestamp, recurringWeekdays: [] });
          return;
        }
        if (isEmpty(appointmentTiming)) {
          this.updateActivity({ scheduledStartTimestamp: null, recurringWeekdays: [] });
          return;
        }
        if (isArray(appointmentTiming)) {
          this.updateActivity({ recurringWeekdays: appointmentTiming });
        }
      },

    }, // appointmentTiming()

    duration() {
      return get(this.modelValue, 'duration');
    },

    userInfo: {
      get() {
        return {
          user: get(this.modelValue, 'user'),
          userName: get(this.modelValue, 'userName'),
        };
      },
      set(userInfo) {
        const user = get(userInfo, 'user', null);
        const userName = get(userInfo, 'userName', null);
        this.updateActivity({ user, userName });
      },
    },

    scheduledStartTimestamp: {
      get() {
        return get(this.modelValue, 'scheduledStartTimestamp');
      },
      set(ts) {
        if (ts !== null) {
          this.updateActivity({ scheduledStartTimestamp: ts });
        }
      },
    },

    scheduledEndTimestamp: {
      get() {
        const scheduledStartTimestamp = get(this.modelValue, 'scheduledStartTimestamp');
        if (!(scheduledStartTimestamp instanceof Timestamp)) {
          return null;
        }
        return Timestamp.fromDate(
          this.addToDate(scheduledStartTimestamp.toDate(), { seconds: this.duration }),
        );
      },
      set(ts) {
        const scheduledStartTimestamp = get(this.modelValue, 'scheduledStartTimestamp');
        if ((!(scheduledStartTimestamp instanceof Timestamp))
          || (!(ts instanceof Timestamp))) {
          return;
        }
        let duration = this.getDateDiff(ts.toDate(), scheduledStartTimestamp.toDate(), 'seconds');
        if (duration < 0) {
          duration += 24 * 60 * 60;
        }
        this.updateActivity({ duration });
      },
    },

  },

  mounted() {
    this.loadClinicUnits();
  }, // mounted(

  methods: {
    ...mapActions(units, ['loadClinicUnits']),

    onInputAppointmentTiming(appointmentTiming) {
      this.appointmentTiming = appointmentTiming;
    }, // onInputAppointmentTiming()

    updateActivity(activityObj) {
      this.$emit('update:modelValue', { ...this.modelValue, ...activityObj });
    },

  },

};
</script>
